import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import { bool, number, string, func } from 'prop-types';
import PasswordInput from '../../common/PasswordInput';
import { Errors } from '../../../constants/errors';
import { login } from '../../../services/Api/User/UserService';
import {
  LOYALTY_SIGNUP_MODAL,
  PUBLIC_ROUTE_REGEX
} from '../../../constants/defaults';
import { AuthContext } from '../../../Contexts';
import toast from '../../Toaster/toast';

const EnterPassWordModal = ({
  email,
  redirectUri,
  quantity,
  isFromHomeDelivery,
  hideAuthenticateModal
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  let lang = i18n.language;
  const [pwdState, setPwdState] = useState({
    password: '',
    passwordErr: ''
  });

  const savedLinkClicked = useSelector(
    state => state.userDrugs.savedLinkClicked
  );

  const [showLoader, setLoader] = useState(false);
  const [apiError, setApiError] = useState('');
  const { password, passwordErr } = pwdState;
  const modalState = useSelector(state => state.modal.modal);
  const context = useContext(AuthContext);
  const redirectUrl = url => {
    return _.isEmpty(url)
      ? ''
      : url.replace(
          new RegExp(
            '{{DisplayQuantity}}'.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
            'g'
          ),
          quantity
        );
  };

  const onPwdInputChange = event => {
    const { name, value } = event.target;
    setPwdState({
      ...pwdState,
      [name]: value,
      passwordErr: Errors['']
    });
  };

  const onPwdInputBlur = event => {
    const { value } = event.target;
    setPwdState({
      ...pwdState,
      passwordErr: _.isEmpty(value.trim()) ? Errors['Required'] : Errors['']
    });
  };

  const setPassWordError = (customError = '') => {
    setPwdState({
      ...pwdState,
      password: '',
      passwordErr: !_.isEmpty(customError)
        ? customError
        : t('Please enter a valid password')
    });
    setLoader(false);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (showLoader) {
      return;
    } else {
      if (_.isEmpty(password.trim())) {
        setPwdState({
          ...pwdState,
          passwordErr: Errors['Required']
        });
        return;
      }
      _.triggerGTMVirtualPageViewEvent({
        event: 'signInSubmitClick',
        formId: 'sign in',
        clickText: 'Sign in'
      });
      setLoader(true);
      const loginData = {
        email,
        password,
        tenantId: 0
      };
      try {
        const loginResponse = await login(loginData);
        if (loginResponse && loginResponse.status === 200) {
          modalState !== LOYALTY_SIGNUP_MODAL && hideAuthenticateModal();
          /*
           after login set user data and call api to get user points
         */
          _.setUserData({}, dispatch, savedLinkClicked);
          context?.setUserData(loginResponse?.data);
          _.setUserPoints(loginResponse, dispatch);
          isFromHomeDelivery
            ? window.open(redirectUrl(redirectUri), '_self')
            : history.location.pathname.match(
                /how-it-works|como-funciona-singlecare/
              ) && history.push(_.withLocale(lang, '/'));
          //: window.location.reload(); // ToDo check this and remove this
          _.triggerGTMVirtualPageViewEvent({
            event: 'signInSuccess',
            formId: 'sign in'
          });
          toast.success(t('Logged In'));

          setTimeout(() => {
            if (history.location.pathname.match(PUBLIC_ROUTE_REGEX)) {
              history.push(_.withLocale(lang, '/'));
            }
            if (location && location.state && location.state.prevPath) {
              history.push(`${location.state.prevPath}`);
            }
          }, 1000);
        }
        setLoader(false);
      } catch (err) {
        if (err.response && err.response.status) {
          if (
            err.response.status === 400 &&
            err.response.data.error.includes('validateSigninRequest.Password')
          ) {
            setPassWordError();
            return;
          }
          if (
            err.response.status === 400 &&
            (err.response.data.error === 'Bad Request' ||
              err.response.data.error === 'Invalid Password')
          ) {
            setPassWordError();
            return;
          }
          if (
            err.response.status === 403 &&
            (err.response.data.error === 'Forbidden' ||
              err.response.data.error === 'AuthenticationFailed')
          ) {
            setPassWordError(t('Incorrect username or password.'));
            return;
          }
          if (
            err.response.status === 403 &&
            err.response.data.error === 'UserAccountLocked'
          ) {
            setApiError(t('User account lock error text'));
            return;
          }
        }
        if (err.response && err.response.data) {
          setApiError(err.response.data.message);
        } else {
          setApiError(err.message);
        }
      } finally {
        setLoader(false);
      }
    }
  };

  const handleMouseDown = () => {
    localStorage.setItem('backPath', location.pathname);
  };

  return (
    <div className="enterPassWordModal">
      <div>
        <h2 id="dialogTitleHeading" className="deliveyHeading">
          {t('Welcome back!')}
        </h2>
        <h2 className="registerHeading">
          {t("You're already registered. Please")}&nbsp;{t('enter your')}&nbsp;
          {t('password.')}
        </h2>
      </div>
      <form className="commonForm" onSubmit={handleSubmit}>
        <PasswordInput
          parentClass="passwordInput"
          password={password}
          name="password"
          error={passwordErr}
          label={t('Password')}
          onInputChange={onPwdInputChange}
          onInputBlur={onPwdInputBlur}
          showInfoIcon={false}
          showConfirmMatch={false}
          autoFocus={true}
        />
        {!_.isEmpty(apiError) && <p className="errorLabel">{apiError}</p>}
        {showLoader ? (
          <img
            src="/content/images/loader/loading.gif"
            alt="loader"
            className="searchBarLoader"
          />
        ) : (
          <button className="dialogComponent button pink">
            {t('Sign in')}
          </button>
        )}
      </form>
      <a
        href={_.withLocale(lang, '/forgot-password')}
        className="dialogComponent resetLink"
        onMouseDown={handleMouseDown}
      >
        {t('Reset password?')}
      </a>
    </div>
  );
};

EnterPassWordModal.propTypes = {
  email: string,
  redirectUri: string,
  quantity: number,
  isFromHomeDelivery: bool,
  hideAuthenticateModal: func
};

EnterPassWordModal.defaultProps = {
  email: '',
  redirectUri: '',
  quantity: 0,
  isFromHomeDelivery: false,
  hideAuthenticateModal: () => {}
};

export default EnterPassWordModal;
